import { Link } from '@tanstack/react-router';
import {
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CircleArrowLeft, Download, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  downloadUpcomingPaymentsCSVAPI,
  useFetchUpcomingPayments,
} from '~entities/payment/api/upcoming-payments';

import { useGetCurrency } from '~shared/api/currency-api';
import { InputDatePicker } from '~shared/components/input-date-picker';
import { INPUT_DATE_PICKER_ICON_POSITION } from '~shared/components/input-date-picker.types';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

import { UpcomingPaymentsTable } from './upcoming-payments-table';

export const UpcomingPaymentsPage = () => {
  const [dueDate, setDueDate] = useState<string>(() =>
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: currencyData } = useGetCurrency();

  const {
    data,
    isFetching: isFetchingPayments,
    error,
    refetch,
  } = useFetchUpcomingPayments({
    dueDate,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  });

  const formatCurrency = useMemo(() => {
    const currency = currencyData?.name ?? 'USD';
    const symbol = currencyData?.symbol ?? '$';

    return (amount: number) => {
      if (symbol && symbol !== currency) {
        return `${symbol}${amount.toFixed(2)}`;
      }

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    };
  }, [currencyData?.name, currencyData?.symbol]);

  const handleDateChange = (newDate: string) => {
    if (!newDate) {
      return;
    }

    setDueDate(newDate);
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  const handleDownloadCSV = async () => {
    try {
      const blob = await downloadUpcomingPaymentsCSVAPI({
        dueDate,
        page: 1,
        limit: 10,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `upcoming-payments-${dueDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  if (error) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-4'>
          <Link to='/dashboard' className='flex items-center gap-2'>
            <CircleArrowLeft className='size-6' />
            <h2 className='text-2xl font-semibold'>Upcoming Payments</h2>
          </Link>
        </div>
        <div className='mb-6 mt-8 border-t' />
        <Card>
          <CardContent className='p-6'>
            <div className='text-center text-red-600'>
              Error loading upcoming payments. Please try again.
            </div>
            <div className='mt-4 text-center'>
              <Button onClick={() => refetch()}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-4'>
        <Link to='/dashboard' className='flex items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Upcoming Payments</h2>
        </Link>
      </div>
      <div className='mb-6 mt-8 border-t' />

      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Search className='size-5' />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className='min-w-0'>
          <div className='flex flex-col gap-2'>
            <label htmlFor='dueDate' className='text-sm font-medium'>
              Due Date
            </label>
            <div className='flex items-center gap-2'>
              <InputDatePicker
                id='dueDate'
                value={dueDate}
                onChange={handleDateChange}
                ariaLabel='Due date'
                allowClear={false}
                displayFormat='MM/dd/yyyy'
                iconPosition={INPUT_DATE_PICKER_ICON_POSITION.END}
                className='h-8 min-w-0 flex-1 px-2 sm:max-w-48'
              />
              <Button
                onClick={() => refetch()}
                variant='outline'
                size='icon'
                className='size-8 shrink-0'
                title='Refresh'
                aria-label='Refresh'
              >
                <RefreshCw className='size-4' />
              </Button>
              <Button
                onClick={handleDownloadCSV}
                variant='outline'
                size='icon'
                className='size-8 shrink-0'
                title='Download CSV'
                aria-label='Download CSV'
              >
                <Download className='size-4' />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
          <Card>
            <CardContent className='p-4'>
              <div className='text-sm font-medium text-gray-600'>
                Total Payments
              </div>
              <div className='text-2xl font-bold'>{data.pagination.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='p-4'>
              <div className='text-sm font-medium text-gray-600'>
                Total Amount
              </div>
              <div className='text-2xl font-bold'>
                {formatCurrency(
                  data.data.reduce(
                    (sum, payment) => sum + payment.finalAmount,
                    0,
                  ),
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Upcoming Payments</CardTitle>
        </CardHeader>
        <CardContent className='min-w-0'>
          <UpcomingPaymentsTable
            formatCurrency={formatCurrency}
            isUpdating={isFetchingPayments}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            pagination={pagination}
            payments={data?.data ?? []}
            rowCount={data?.pagination.total ?? 0}
            sorting={sorting}
          />
        </CardContent>
      </Card>
    </div>
  );
};
